import type { DelegateStatus, SessionStatus } from "./types";
import { formatReviewDraft, parseReviewDraft } from "./reviewReferences";
export type AstraEffort = "high" | "xhigh" | "max";
const EFFORT_LABEL = { high: "High", xhigh: "xHigh", max: "Max" } as const;

export function delegateDraftCommand(draft: string, app: "operations" | "build") {
  draft = parseReviewDraft(draft).text;
  const command = /^\/(codex-delegate-sample-creative|codex-delegate-sample|codex-delegate-2)(?=\s|$)/.exec(draft)?.[0];
  return command && (app === "operations" || command === "/codex-delegate-2") ? command : null;
}

export function delegateDraftOptions(draft: string, app: "operations" | "build") {
  draft = parseReviewDraft(draft).text;
  const command = delegateDraftCommand(draft, app);
  if (!command) return null;
  const tail = draft.slice(command.length);
  if (command === "/codex-delegate-sample-creative") {
    return { command, effort: "max" as AstraEffort, sampleProfile: "creative" as const,
      invalid: /^\s*--/.test(tail), ownedLength: command.length, rest: tail };
  }
  const option = /^[ \t]+--effort[ \t]+(high|xhigh|max)(?=\s|$)/.exec(tail);
  const rest = tail.slice(option?.[0].length ?? 0);
  const invalid = /^\s*--/.test(rest);
  return { command, effort: (option?.[1] ?? "max") as AstraEffort, invalid,
    ownedLength: command.length + (option?.[0].length ?? 0), rest };
}

/** Requested-provider title only. Never attributes historical/native execution. */
export function requestedProgressName(status: SessionStatus | undefined, companyId: string | null,
  draft: string, app: "operations" | "build"): "Astra" | "Opus" | "Claude" | "Agent" {
  const delegate = delegateDraftOptions(draft, app);
  if (delegate && !delegate.invalid) return "Astra";
  if (!companyId || status?.companyId !== companyId) return "Agent";
  const model = status.requestedModel ?? "";
  if (/^claude-opus-\d+(?:[.-]\d+)*$/.test(model)) return "Opus";
  return /^claude-[a-z0-9.-]+$/.test(model) ? "Claude" : "Agent";
}

/** Creative is automatic; restoring any draft must not request a dialog. */
export function shouldOpenDelegateEffortPicker(previousDraft: string, nextDraft: string, app: "operations" | "build") {
  previousDraft = parseReviewDraft(previousDraft).text;
  nextDraft = parseReviewDraft(nextDraft).text;
  const previous = delegateDraftOptions(previousDraft, app);
  const next = delegateDraftOptions(nextDraft, app);
  // This command prefixes Creative. Wait for its delimiter so manual typing
  // can reach "-creative" without the original Sample sheet taking focus.
  if (next?.command === "/codex-delegate-sample" && nextDraft === next.command) return false;
  return !!next && !next.invalid && next.sampleProfile !== "creative" &&
    (!previous || previous.command !== next.command || previousDraft === "/codex-delegate-sample");
}

export function setDelegateDraftEffort(state: {
  activeCompanyId: string | null; composerDraft: string; setComposerDraft(value: string): void;
}, expectedCompanyId: string | null, app: "operations" | "build", effort: string) {
  if (!expectedCompanyId || state.activeCompanyId !== expectedCompanyId || !Object.hasOwn(EFFORT_LABEL, effort)) return;
  const parsed = delegateDraftOptions(state.composerDraft, app);
  if (!parsed || parsed.invalid || parsed.sampleProfile === "creative") return;
  state.setComposerDraft(formatReviewDraft(parsed.command + " --effort " + effort + parsed.rest,
    parseReviewDraft(state.composerDraft).references));
}

/** Remove only the leading token. Whitespace, task text and all other state stay. */
export function removeDelegateDraft(draft: string, app: "operations" | "build") {
  const parsed = delegateDraftOptions(draft, app);
  const review = parseReviewDraft(draft);
  return parsed ? formatReviewDraft(review.text.slice(parsed.ownedLength), review.references) : draft;
}

export function clearDelegateDraft(state: {
  activeCompanyId: string | null; composerDraft: string; setComposerDraft(value: string): void;
}, expectedCompanyId: string | null, app: "operations" | "build") {
  if (!expectedCompanyId || state.activeCompanyId !== expectedCompanyId) return;
  const next = removeDelegateDraft(state.composerDraft, app);
  if (next !== state.composerDraft) state.setComposerDraft(next);
}

export function currentDelegateRequestId(
  messages: { id: string; role: string }[],
  queue: { id: string; userMsgId?: string; dispatching?: boolean }[],
) {
  const waiting = new Set(queue.filter(item => !item.dispatching).flatMap(item => [item.id, item.userMsgId]));
  return [...messages].reverse().find(message => message.role === "user" &&
    !/^(u-resume-|u-bootsweep-)/.test(message.id) && !waiting.has(message.id))?.id;
}

/** Source is the authenticated status projection, never a chat/tool string. */
export function delegatePresentation(status: SessionStatus | undefined, companyId: string,
  requestMessageId: string | undefined, now = Date.now()) {
  const value: DelegateStatus | undefined = status?.delegate;
  const effort = value?.rootEffort ?? value?.workEffort;
  if (!value || value.source !== "delegate-runtime" || value.companyId !== companyId ||
      status?.companyId !== companyId || !requestMessageId || value.requestMessageId !== requestMessageId ||
      value.model !== "gpt-6-astra" || !effort || !Object.hasOwn(EFFORT_LABEL, effort) ||
      value.workEffort !== effort || value.qaEffort !== "high" ||
      !Number.isFinite(Date.parse(value.updatedAt)) || Date.parse(value.updatedAt) > now + 300_000) return null;
  const active = ["waiting", "running", "verifying"].includes(value.status);
  if (active && (status.busy !== true || status.state !== "running")) return null;
  if (!active && now - Date.parse(value.updatedAt) > 15_000) return null;
  const suffix = ({ waiting: "Queued", running: "", verifying: "Checking", failed: "Failed",
    cancelled: "Stopped", completed: "Finished" } as const)[value.status];
  if (suffix === undefined) return null;
  return { label: "Astra · " + (value.sampleProfile === "creative" ? "Creative" : EFFORT_LABEL[effort]) + (suffix ? " · " + suffix : ""), canStop: active,
    title: "Configured Astra " + EFFORT_LABEL[effort] + " worker · " +
      (value.childEffort === "high" ? "parallel agents High. " : "independent QA High. ") +
      (value.status === "waiting" ? "Waiting for admission; model work has not started." :
       value.status === "completed" ? "The delegated run finished. See its answer for actual results and limitations." :
       value.status === "failed" || value.status === "cancelled" ? "The run ended without a completion claim." :
       "Runtime status, not a model or task verification badge.") };
}
